import type { Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { useLazyLayerMetaModel } from '../context/useLazyLayerMetaModel'
import { useNavigationParams } from '../system/useNavigationParams'
import {
  isEnumMetaType,
  isModelMetaType,
  type MetaModel,
  type MetaType,
} from '../types/metaModel'
import { useConfigurationDocuSiteUrl } from './useConfigurationDocuSiteUrl'

// oxlint-disable-next-line regexp/no-super-linear-backtracking
const documentationLinkRegex =
  /@see(?::(?<category>openapi|asyncapi))?\s+(?<project>\w+)(?:-(?:shared|server))?\/(?:(?<packageid>pkg__.*?)|(?<diagram>diag__.*?)|(?<type>\w+)(?:\.(?<navigation>\w+))?|(?<servicetag>.+?))\s*(?:,\s*(?<prettyname>.+?))?@/g // NOSONAR -> This is only parsing data from the configuration and not user input/data as such vulnerability due to backtracking is not a concern

const markdownLinkRegex = /\[(?<name>[^\]]*)\]\(\.\.\/\.\.\/(?<link>.*?)\.md\)/g // NOSONAR -> This is only parsing data from the configuration and not user input/data as such vulnerability due to backtracking is not a concern

export type DocumentationSiteLinkTransformer = (
  documentation: Optional<string>,
) => Optional<string>

export const useDocumentationSiteLinkTransformer =
  (): DocumentationSiteLinkTransformer => {
    const docuSiteUrl = useConfigurationDocuSiteUrl()
    const { layer: currentLayer } = useNavigationParams()

    const metaModel = useLazyLayerMetaModel(currentLayer?.layerId)

    return useCallback(
      (documentation: Optional<string>) => {
        if (!documentation || !docuSiteUrl) {
          return documentation
        }

        const linksReplaced = documentation.replace(
          documentationLinkRegex,
          (match, ...args) => {
            const groups = args[args.length - 1]

            if (groups) {
              return transformGroupsToLink({
                match,
                docuSiteUrl,
                groups,
                metaModel,
              })
            }

            return match
          },
        )

        return linksReplaced.replace(markdownLinkRegex, (match, ...args) => {
          const { name, link } = args[args.length - 1] as Record<
            string,
            Optional<string>
          >

          if (name && link) {
            return `[${name}](${docuSiteUrl}/${link})`
          }

          return match
        })
      },
      [docuSiteUrl, metaModel],
    )
  }

const transformGroupsToLink = ({
  match,
  docuSiteUrl,
  groups,
  metaModel,
}: {
  match: string
  docuSiteUrl: string
  groups: Record<string, Optional<string>>
  metaModel: MetaModel
}) => {
  const {
    category,
    project,
    packageid,
    diagram,
    type,
    navigation,
    servicetag,
    prettyname,
  } = groups
  const projectWithRealName =
    project === 'account' || project === 'business' ? 'life' : project

  if (!category) {
    if (type) {
      const metaType = metaModel.types.get(type)

      if (!metaType) {
        return match
      }

      const packageName = filterPackageNameBasePackage(getPackageName(metaType))

      const typeWithPackage = packageName ? `${packageName}.${type}` : `${type}`

      const linkName = prettyname ?? type

      const typeWithPackageAndNavigation = navigation
        ? `${typeWithPackage}#${navigation}`
        : `${typeWithPackage}`

      return `[${linkName}](${docuSiteUrl}/${projectWithRealName}/types/${typeWithPackageAndNavigation})`
    } else if (packageid) {
      const linkName = prettyname ?? packageid.substring(5)

      return `[${linkName}](${docuSiteUrl}/${projectWithRealName}/types/${packageid})`
    } else if (diagram) {
      const linkName = prettyname ?? diagram.substring(6)

      return `[${linkName}](${docuSiteUrl}/${projectWithRealName}/types/${diagram})`
    }
  } else if (category === 'openapi') {
    const serviceEnding = getServiceEnding({ servicetag, type, navigation })

    if (serviceEnding) {
      const linkName = prettyname ?? serviceEnding

      return `[${linkName}](${docuSiteUrl}/openapi/${projectWithRealName}/${serviceEnding})`
    }
  } else if (category === 'asyncapi') {
    const serviceEnding = getServiceEnding({ servicetag, type, navigation })

    if (serviceEnding) {
      const linkName = prettyname ?? serviceEnding

      return `[${linkName}](${docuSiteUrl}/asyncapi/${projectWithRealName}/${projectWithRealName}-asyncapi/#${serviceEnding})`
    }
  }

  return match
}

const getServiceEnding = ({
  servicetag,
  type,
  navigation,
}: {
  servicetag: Optional<string>
  type: Optional<string>
  navigation: Optional<string>
}) => {
  if (servicetag) {
    return servicetag
  }

  if (type && navigation) {
    return `${type}.${navigation}`
  }

  return type
}
const getPackageName = (type: MetaType): Optional<string> => {
  if (isModelMetaType(type)) {
    return type.packageName
  }

  if (isEnumMetaType(type)) {
    return type.packageName
  }

  return null
}

const filterPackageNameBasePackage = (
  packageName: Optional<string>,
): Optional<string> => {
  if (packageName && packageName.includes('.shared.')) {
    return packageName.substring(packageName.indexOf('.shared.') + 8)
  }

  if (packageName && packageName.includes('.server.')) {
    return packageName.substring(packageName.indexOf('.server.') + 8)
  }

  return packageName
}
